// src/popup/GlobalSettingsPopup.js
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

// Popup-komponent för att hantera globala inställningar för alla anställda
export default function GlobalSettingsPopup({
  isOpen,
  onClose,
  onSave,
  initialValues = {
    obEnabled: true,
    semEnabled: true,
    semProcent: 12,
    tax: 30,
    hideNetSalary: false,
  },
}) {
  const { currentUser, profile, companyAccountUid } = useAuth();

  // State för inställningar
  const [obEnabled, setObEnabled] = useState(true);
  const [semEnabled, setSemEnabled] = useState(true);
  const [semProcent, setSemProcent] = useState(12);
  const [tax, setTax] = useState(30);
  const [hideNetSalary, setHideNetSalary] = useState(false);

  // Hämta företagskontots UID (använder companyAccountUid om det finns)
  const accountToUse = companyAccountUid || currentUser?.uid;

  // Uppdatera state när popup öppnas eller initialValues ändras
  useEffect(() => {
    if (!isOpen) return;

    setObEnabled(Boolean(initialValues.obEnabled ?? true));
    setSemEnabled(Boolean(initialValues.semEnabled ?? true));
    setSemProcent(Number(initialValues.semProcent ?? 12));
    setTax(Number(initialValues.tax ?? 30));
    setHideNetSalary(Boolean(initialValues.hideNetSalary));
  }, [isOpen, initialValues]);

  // Stäng popup om den inte är öppen
  if (!isOpen) return null;

  // Hantera sparande av inställningar
  const handleSave = () => {
    onSave({
      obEnabled,
      semEnabled,
      semProcent: Math.max(0, Math.min(100, Number(semProcent || 0))),
      tax: Math.max(0, Math.min(100, Number(tax || 0))),
      hideNetSalary,
    });
    onClose();
  };

  // Stilar för komponenten
  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(17, 24, 39, 0.35)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2200,
      padding: 16,
    },
    popup: {
      width: "100%",
      maxWidth: 420,
      background: "#ffffff",
      borderRadius: 16,
      boxShadow: "0 12px 30px rgba(0,0,0,0.16)",
      padding: 24,
      boxSizing: "border-box",
    },
    title: {
      margin: "0 0 8px",
      fontSize: 20,
      fontWeight: 700,
      color: "#111827",
    },
    description: {
      margin: "0 0 20px",
      fontSize: 14,
      color: "#6b7280",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
      alignItems: "center",
    },
    label: {
      fontSize: 13,
      fontWeight: 600,
      color: "#6b7280",
    },
    select: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #d1d5db",
      fontSize: 14,
      background: "#fff",
      cursor: "pointer",
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #d1d5db",
      fontSize: 14,
      boxSizing: "border-box",
      outline: "none",
      transition: "border-color 0.2s",
    },
    buttonGroup: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
      marginTop: 24,
    },
    cancelButton: {
      height: 40,
      padding: "0 16px",
      borderRadius: 8,
      border: "1px solid #d1d5db",
      background: "#ffffff",
      color: "#6b7280",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
    },
    saveButton: {
      height: 40,
      padding: "0 16px",
      borderRadius: 8,
      border: "1px solid #2563eb",
      background: "#3b82f6",
      color: "#ffffff",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
    },
  };

  // Hantera fokus för input-fält
  const handleInputFocus = (e) => {
    e.target.style.borderColor = "#3b82f6";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#d1d5db";
  };

  // Hantera mus-hover för knappar
  const handleButtonHover = (e, isHover) => {
    if (isHover) {
      e.currentTarget.style.opacity = "0.85";
    } else {
      e.currentTarget.style.opacity = "1";
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.popup} onClick={(e) => e.stopPropagation()}>
        {/* Rubrik */}
        <h3 style={styles.title}>
          Inställningar för alla användare
        </h3>

        {/* Beskrivning */}
        <p style={styles.description}>
          Dessa värden sparas på alla anställda i företaget.
          {accountToUse && (
            <span style={{ display: "block", marginTop: "4px", fontSize: "12px", color: "#9ca3af" }}>
              Företags-ID: {accountToUse.substring(0, 8)}...
            </span>
          )}
        </p>

        {/* Inställningsfält */}
        <div style={styles.grid}>
          {/* OB-inställning */}
          <div style={styles.label}>OB</div>
          <select
            value={obEnabled ? "on" : "off"}
            onChange={(e) => setObEnabled(e.target.value === "on")}
            style={styles.select}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            <option value="on">På</option>
            <option value="off">Av</option>
          </select>

          {/* SEM-inställning */}
          <div style={styles.label}>Sem</div>
          <select
            value={semEnabled ? "on" : "off"}
            onChange={(e) => setSemEnabled(e.target.value === "on")}
            style={styles.select}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            <option value="on">På</option>
            <option value="off">Av</option>
          </select>

          {/* SEM-procent */}
          <div style={styles.label}>SEM %</div>
          <input
            type="number"
            min="0"
            max="100"
            value={semProcent}
            onChange={(e) => setSemProcent(e.target.value)}
            style={styles.input}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />

          {/* Skatteprocent */}
          <div style={styles.label}>Skatt %</div>
          <input
            type="number"
            min="0"
            max="100"
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            style={styles.input}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />

          <div style={styles.label}>Nettolön</div>
          <select
            value={hideNetSalary ? "hidden" : "visible"}
            onChange={(e) => setHideNetSalary(e.target.value === "hidden")}
            style={styles.select}
          >
            <option value="visible">Visa alla</option>
            <option value="hidden">Dölj alla</option>
          </select>
        </div>

        {/* Knappar */}
        <div style={styles.buttonGroup}>
          <button
            onClick={onClose}
            style={styles.cancelButton}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            Avbryt
          </button>

          <button
            onClick={handleSave}
            style={styles.saveButton}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            Spara
          </button>
        </div>
      </div>
    </div>
  );
}
